import { Component, Input } from '@builder.io/sdk';
import { ReactChildren } from 'types/global';

import { ResourceConversionPanelReferenceProps } from 'organisms/Cards/ResourceConversionPanel/types';

const tocItemFields = [
  {
    name: 'id',
    type: 'string',
    required: true,
    showIf: 'false',
    helperText: 'Internally generated ID for each TOC item.',
  },
  {
    name: 'label',
    type: 'string',
    defaultValue: 'Heading',
    helperText: 'Visible text for the table of contents entry.',
  },
  {
    name: 'link',
    type: 'string',
    defaultValue: '#heading',
    helperText: 'Href for the entry (e.g. #section-id or absolute/relative URL).',
  },
  {
    name: 'scrollLink',
    type: 'string',
    required: false,
    helperText: 'Optional: custom selector/anchor for smooth scroll if different from "link".',
  },
] as const satisfies DeepReadonly<Input[]>;

const articleFields = [
  {
    name: 'tocHeading',
    type: 'string',
    defaultValue: 'In this article',
    helperText: 'Heading text displayed above the table of contents.',
  },
  {
    name: 'showProgressBar',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Display an animated progress indicator bar at the top showing reading progress through the article.',
  },
  {
    name: 'enableLockedContent',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Enable progressive content unlock with email gate.',
  },
  {
    name: 'pageId',
    type: 'string',
    required: false,
    helperText: 'Unique identifier for this locked page (used for localStorage persistence and analytics tracking).',
    showIf: "options.get('enableLockedContent') === true",
  },
  {
    name: 'lockHeadlineId',
    type: 'string',
    required: false,
    helperText:
      'Optional: ID of the specific heading where the lock should appear (e.g., "why-buy-embedded-ipaas"). If provided, this takes priority over Lock Percentage. Leave empty to use percentage-based locking.',
    showIf: "options.get('enableLockedContent') === true",
  },
  {
    name: 'headingCutDivisor',
    type: 'number',
    defaultValue: 1.4,
    helperText:
      'Controls where the heading is cut when using Lock Headline ID. Lower values (e.g., 1.2) show more of the heading, higher values (e.g., 2.0) show less. Default: 1.4',
    showIf: "options.get('enableLockedContent') === true && options.get('lockHeadlineId')",
  },
  {
    name: 'lockPercentage',
    type: 'number',
    defaultValue: 25,
    helperText:
      'Percentage of article content visible before lock (e.g., 25 = first 25% is free). Only used if Lock Headline ID is not provided. This applies to all screen sizes unless mobile/desktop specific values are set.',
    showIf: "options.get('enableLockedContent') === true && !options.get('useSeparatePercentages')",
  },
  {
    name: 'useSeparatePercentages',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Enable to set different lock percentages for mobile and desktop.',
    showIf: "options.get('enableLockedContent') === true",
  },
  {
    name: 'lockPercentageMobile',
    type: 'number',
    defaultValue: 25,
    helperText:
      'Percentage of article content visible before lock on mobile devices (screen width < 768px). Only used if Lock Headline ID is not provided.',
    showIf: "options.get('enableLockedContent') === true && options.get('useSeparatePercentages') === true",
  },
  {
    name: 'lockPercentageDesktop',
    type: 'number',
    defaultValue: 25,
    helperText:
      'Percentage of article content visible before lock on desktop devices (screen width >= 768px). Only used if Lock Headline ID is not provided.',
    showIf: "options.get('enableLockedContent') === true && options.get('useSeparatePercentages') === true",
  },
  {
    name: 'lockHeading',
    type: 'string',
    defaultValue: "You've read 25% of this guide",
    helperText: 'Heading displayed in the unlock banner.',
    showIf: "options.get('enableLockedContent') === true",
  },
  {
    name: 'lockDescription',
    type: 'longText',
    defaultValue: 'Enter your work email to unlock the full guide and continue reading.',
    helperText: 'Description text displayed in the unlock banner.',
    showIf: "options.get('enableLockedContent') === true",
  },
  {
    name: 'unlockForm',
    type: 'reference',
    model: 'form',
    helperText: 'Marketo form reference for content unlock.',
    showIf: "options.get('enableLockedContent') === true",
  },
  {
    name: 'manualToc',
    type: 'boolean',
    defaultValue: false,
    helperText: 'Enable to define the TOC manually instead of auto-generating it.',
  },
  {
    name: 'tableOfContents',
    type: 'list',
    defaultValue: [],
    subFields: tocItemFields,
    helperText: 'List of TOC items when "Manual TOC" is enabled. Define label and destination per section.',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onChange: (options: Map<string, any>) => {
      if (options.get('tableOfContents').length > 0) {
        options.get('tableOfContents').forEach((item: Map<string, string>, index: number) => {
          item.set('id', `contents-${index}`);
        });
      }
    },
    showIf: "options.get('manualToc') === true",
  },
  {
    name: 'conversionPanel',
    type: 'reference',
    model: 'resource-conversion-panel',
    helperText: 'Reference to the conversion panel component displayed in the article.',
  },
  {
    name: 'company',
    type: 'reference',
    model: 'company',
    helperText: 'Reference to the company entity associated with this content.',
  },
  {
    name: 'resourceConversionPanelTheme',
    type: 'string',
    enum: ['green'],
    helperText: 'Visual theme applied to the conversion panel (currently only "green").',
  },
] as const satisfies DeepReadonly<Input[]>;

const articleConfig = {
  name: 'Article',
  inputs: articleFields,
  canHaveChildren: true,
  childRequirements: {
    message: 'that aint gonna work',
    query: {
      'component.name': {
        $in: [
          'articleAccordion',
          'Calculator',
          'callOutCard',
          'codeBlock',
          'contentTable',
          'image',
          'quoteCard',
          'ResourceConversionPanel',
          'resourceDownloadCard',
          'richText',
          'statsCard',
          'Video',
          'VideoCustom',
        ],
      },
    },
  },
} as Component;

export type TocItemField = GenerateBuilder<typeof tocItemFields>;
export interface ArticleConfigProps
  extends
    Omit<GenerateBuilder<typeof articleFields>, 'conversionPanel' | 'company' | 'unlockForm'>,
    ResourceConversionPanelReferenceProps,
    CompanyCardFieldProps,
    ReactChildren {
  showProgressBar?: boolean;
  enableLockedContent?: boolean;
  pageId?: string;
  lockHeadlineId?: string;
  headingCutDivisor?: number;
  lockPercentage?: number;
  useSeparatePercentages?: boolean;
  lockPercentageMobile?: number;
  lockPercentageDesktop?: number;
  lockHeading?: string;
  lockDescription?: string;
  unlockForm?: BuilderReference<FormReference>;
}

export default articleConfig;
